// =============================================================================
// ui/tabs/ReimportCard.tsx — Settings → Backup & Restore → Re-import.
// -----------------------------------------------------------------------------
// Beta's Re-import card, which holds two controls (app/index.html:49793):
//
//   Re-import File…     replace one file's worth of trades
//   Wipe All Imported   take every parser-imported trade back
//
// and promises, in its own blurb, that "Manual + synthetic-close trades
// preserved."
//
// ── 🚨 RE-IMPORT IS ROUTED HERE, NOT REBUILT HERE ──
//
// The capability already exists and is BETTER than beta's, which is why this
// card renders the two components the Trades tab renders instead of a third
// implementation of either:
//
//   `ImportHistory`  remove one import, chosen by file, with a verdict saying
//                    what that import is the only record of
//   `ImportTrades`   the same parse → preview → confirm path, so the replacement
//                    file goes through the dedupe and the rejected-row preview
//
// ⚠ Beta matches a file's scope by SOURCE ACCOUNT + DATE RANGE and replaces
// every row inside it. **D143 says not to port that.** Two loggers on one
// account split the same position into different numbers of fills, so a range
// sweep removes rows a second source is the only record of — the exact way
// Josh's worst session came to read −$130 when it was −$1,462. The unit of the
// decision is an import, and `ImportHistory` already makes it at that unit and
// shows the position-level verdict D143 asked for.
//
// 🔴 SO WHAT IS PORTED IS THE ROUTE, NOT A SECOND ENGINE — which is the lesson
// of D258: the export was never missing either, only unreachable from the pane
// a user opens looking for it, and a whole duplicate got built and reverted on
// the strength of that.
//
// ── ⚠ AND WIPE ALL IMPORTED IS GENUINELY NEW, FOR ONE MEASURED REASON ──
//
// Undo Import is per BATCH, and a batch only exists for a trade this client's
// importer stamped. `io/sync/importBatches.ts` says unstamped trades never
// cross the wire, and that on a book where only recent imports are stamped that
// is most of it. On a book carried over from beta, Import History therefore
// cannot see the majority of what a parser wrote. This control is the only one
// that reaches those rows — see `domain/importedTrades.ts` for which mark it
// reads and why it is the other one.
// =============================================================================

import { useCallback, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { useDemo } from '../state/demo';
import { ImportHistory } from './ImportHistory';
import { ImportTrades } from './ImportTrades';
import { planWipeImported, wipeImported } from '../../io/sync/wipeImported';
// ⚠ Through `selectors/`, because `ui/` may not import `domain/` — not even a
// type, and `check:layering` rejects it. That file is a pass-through and says so.
import type { WipeScope } from '../../selectors/importedTrades';
import { reportFailureAs } from '../../io/diagnostics/errorLog';

type Phase = 'idle' | 'counting' | 'confirming' | 'wiping';

export function ReimportCard() {
  const { sb } = useSupabase();
  const demo = useDemo();

  const [phase, setPhase] = useState<Phase>('idle');
  const [scope, setScope] = useState<WipeScope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** The re-import flow is mounted only once asked for — see the note below. */
  const [open, setOpen] = useState(false);
  /** Bumped so the history re-reads after a wipe or an import. */
  const [nonce, setNonce] = useState(0);

  const bump = useCallback(() => setNonce((n) => n + 1), []);

  // ⚠ COUNTS FIRST, and the count is what the confirmation is about. Beta does
  // the same and for the same reason `deleteAccount.ts` does: a destructive
  // control that cannot say how much names nothing the user can weigh.
  const askToWipe = async () => {
    setPhase('counting');
    setError(null);
    setMessage(null);
    try {
      const s = await planWipeImported(sb);
      setScope(s);
      setPhase('confirming');
    } catch (e) {
      setPhase('idle');
      setError(reportFailureAs('wipe imported', e, 'Couldn’t work out what would be removed.'));
    }
  };

  const doWipe = async () => {
    if (!scope) return;
    setPhase('wiping');
    setError(null);
    // ⚠ THE IDS THAT WERE COUNTED, handed straight over. See the io header.
    const r = await wipeImported(sb, scope.wipeIds);
    setPhase('idle');
    setScope(null);
    bump();

    if (r.error) {
      // ⚠ Reports the partial count rather than a bare failure, and does NOT
      // claim success — the rows that did go are gone.
      setError(r.error);
      return;
    }
    setMessage(
      `Removed ${r.deleted} imported trade${r.deleted === 1 ? '' : 's'}. `
      + 'Drop your broker files below to import them again.',
    );
  };

  if (demo.active) {
    return (
      <section className="card">
        <h3>Re-import</h3>
        <p className="muted">
          Leave the Demo Account to re-import or wipe imported trades. The demo
          book is a sample that is never saved, so neither control would touch
          anything of yours.
        </p>
      </section>
    );
  }

  return (
    <section className="card">
      <h3>Re-import</h3>
      <p className="muted">
        Redo an import that went wrong. Remove one file’s trades below and drop
        the corrected file, or take every imported trade back and start again.
      </p>
      {/* 🚨 BETA'S OWN PROMISE, kept and said in the same place it says it.
          Both controls leave hand-entered trades alone, and both leave synthetic
          closes alone — those are the app's reconstruction of an exit no broker
          file contains, so no re-import can put one back. */}
      <p className="muted">
        Neither touches anything you typed in yourself, and neither touches a
        synthetic close — that exit was reconstructed here, not read from a
        file, so re-importing cannot bring one back.
      </p>

      {error ? <p className="red" role="alert">{error}</p> : null}
      {message ? <p className="muted" role="status">{message}</p> : null}

      {/* ── Wipe All Imported ───────────────────────────────────────────── */}
      <div className="reimport-block">
        {phase === 'confirming' && scope ? (
          <>
            <span className="muted reimport-confirm">
              {scope.wipeIds.length === 0 ? (
                <>Nothing to wipe — no imported trades in your book.</>
              ) : (
                <>
                  Delete all <b>{scope.wipeIds.length.toLocaleString()}</b>{' '}
                  parser-imported trade{scope.wipeIds.length === 1 ? '' : 's'},
                  with any notes, grades and patterns on them? Keeping{' '}
                  {scope.manual.toLocaleString()} you entered by hand and{' '}
                  {scope.synthetic.toLocaleString()} synthetic close
                  {scope.synthetic === 1 ? '' : 's'}. This cannot be undone.
                </>
              )}
            </span>
            <button type="button" className="btn" disabled={phase !== 'confirming'}
              onClick={() => { setScope(null); setPhase('idle'); }}>Cancel</button>
            {scope.wipeIds.length > 0 ? (
              <button type="button" className="btn danger" onClick={() => void doWipe()}>
                Yes, wipe {scope.wipeIds.length.toLocaleString()}
              </button>
            ) : null}
          </>
        ) : (
          <button type="button" className="btn danger"
            disabled={phase === 'counting' || phase === 'wiping'}
            onClick={() => void askToWipe()}>
            {phase === 'counting' ? 'Counting…'
              : phase === 'wiping' ? 'Removing…'
                : 'Wipe All Imported'}
          </button>
        )}
      </div>

      {/* ── Re-import: remove one import, then drop the replacement ─────── */}
      {/* 🚨 OPENED ON DEMAND, AND THAT IS NOT A STYLE CHOICE. The two borrowed
          components READ ON MOUNT — the whole import history, and the trades
          around it for the reconcile verdict. Rendering them inline made
          opening Settings issue two more queries for a control most visits
          never touch, and on a failed read each one raised its own alert on a
          pane that already has one. Re-importing is a rare, deliberate act; it
          can afford a press. */}
      <div className="reimport-flow">
        <h4>Replace one file</h4>
        <p className="muted">
          Remove the import you want to redo, then drop the corrected file. Both
          steps are the same ones the Trades tab uses — the same history and the
          same importer, on the pane beta keeps them on.
        </p>
        {open ? (
          <>
            <ImportHistory nonce={nonce} onUndone={bump} />
            <ImportTrades onImported={bump} />
          </>
        ) : (
          <button type="button" className="btn" onClick={() => setOpen(true)}>
            Replace one file…
          </button>
        )}
      </div>
    </section>
  );
}
